import { useEffect } from "react";
import type { OfferSection } from "@/hooks/use-offer-sections";
import { focusedOfferLineId } from "@/components/offer/offer-line-nav";

interface UseOfferKeyboardShortcutsArgs {
  sections: OfferSection[];
  canSave: boolean;
  /** Kalles ved ⌘S. Bare når canSave=true. */
  onSave: () => void;
  /** Kalles ved ⌘L (åpne katalog mot hovedbudsjett). */
  onOpenCatalog: () => void;
  /** Kalles ved ⌘⇧L (åpne katalog mot siste tilvalg-seksjon). null = ingen seksjon finnes. */
  onOpenCatalogForSection: (sectionId: string | null) => void;
  /** Kalles ved ? (åpne keyboard-help). */
  onOpenKeyboardHelp: () => void;
  /** Kalles ved ⌘D når en linje har fokus. */
  onDuplicateLine?: (lineId: string) => void;
  /** Kalles ved ⌥↑ / ⌥↓ når en linje har fokus (VIB-2543). */
  onMoveLine?: (lineId: string, dir: "up" | "down") => void;
  /**
   * Når false er tilbudet read-only: redigerings-snarveier (⌘S, ⌘L, ⌘⇧L)
   * deaktiveres. Hjelp-snarveien (?) forblir aktiv. Default true.
   * VIB-offer-author-gating.
   */
  canEdit?: boolean;
}

/** Hjelpefunksjon: ignorer hotkeys når fokus er i et inputfelt. */
function isTypingInField(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  return !!el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA");
}

/**
 * Konsoliderer keyboard-shortcuts for OfferEditPage:
 * - ⌘S/Ctrl+S: lagre (bare hvis canSave=true)
 * - ⌘L/Ctrl+L: åpne katalog mot hovedbudsjett (også med fokus i en celle, VIB-2096)
 * - ⌘⇧L/Ctrl+⇧L: åpne katalog mot siste tilvalg-seksjon (VIB-702)
 * - ?: åpne keyboard-help-dialog (VIB-702). Ignoreres når fokus er i et felt.
 * - ⌘D/Ctrl+D: dupliser linjen som har fokus.
 * - ⌥↑/⌥↓: flytt linjen som har fokus ett hakk (VIB-2543). Uten Alt går
 *   pilene til samme felt på linjen over/under (OfferLineRow).

 *
 * Splittet ut fra OfferEditPage (VIB-658) for å holde page-orkestratoren
 * under 300 linjer.
 */
export function useOfferKeyboardShortcuts({
  sections,
  canSave,
  onSave,
  onOpenCatalog,
  onOpenCatalogForSection,
  onOpenKeyboardHelp,
  onDuplicateLine,
  onMoveLine,
  canEdit = true,
}: UseOfferKeyboardShortcutsArgs) {
  // ⌘S for å lagre
  useEffect(() => {
    if (!canEdit) return;
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        if (canSave) onSave();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [canEdit, canSave, onSave]);

  // VIB-700: ⌘L åpner katalog-dialogen mot hovedbudsjett
  useEffect(() => {
    if (!canEdit) return;
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && e.key.toLowerCase() === "l") {
        e.preventDefault();
        onOpenCatalog();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [canEdit, onOpenCatalog]);

  // VIB-702: ⌘⇧L åpner katalog med fokus på siste tilvalg-seksjon
  useEffect(() => {
    if (!canEdit) return;
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === "l") {
        e.preventDefault();
        const lastSection = sections.length > 0
          ? [...sections].sort((a, b) => b.sort_order - a.sort_order)[0]
          : null;
        onOpenCatalogForSection(lastSection?.id ?? null);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [canEdit, sections, onOpenCatalogForSection]);

  // ⌘D dupliserer linjen som har fokus (mengde/sats/beskrivelse).
  useEffect(() => {
    if (!canEdit) return;
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && e.key.toLowerCase() === "d") {
        const lineId = focusedOfferLineId(e.target);
        if (!lineId) return;
        e.preventDefault();
        onDuplicateLine?.(lineId);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [canEdit, onDuplicateLine]);

  // ⌥↑ / ⌥↓ flytter linjen som har fokus (VIB-2543). OfferLineRow ignorerer
  // piltaster med Alt, så dette kolliderer ikke med ↑/↓-navigasjonen.
  useEffect(() => {
    if (!canEdit || !onMoveLine) return;
    function onKey(e: KeyboardEvent) {
      if (!e.altKey || e.metaKey || e.ctrlKey || e.shiftKey) return;
      if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;
      const lineId = focusedOfferLineId(e.target);
      if (!lineId) return;
      e.preventDefault();
      onMoveLine(lineId, e.key === "ArrowUp" ? "up" : "down");
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [canEdit, onMoveLine]);

  // VIB-702: ? viser tastatur-snarveier
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "?" && !e.metaKey && !e.ctrlKey && !e.altKey) {
        if (isTypingInField(e.target)) return;
        e.preventDefault();
        onOpenKeyboardHelp();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onOpenKeyboardHelp]);
}
